import React, { useEffect, useState, useRef, useMemo } from 'react';
import { notification, theme } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import ComplianceEstLayout from 'components/Layouts/ComplianceEstLayout';
import CustomTable from 'components/Tables/CustomTable';
import { SolutionColumns } from 'constants/tableColumns';
import { RootState } from 'store/configureStore';
import Helpers from 'utils/Helpers';
import {
  ISolution,
  ISolutionTableRow,
  ISolutions,
  ISolutionsToShow
} from 'store/ducks/Solutions/types';
import { IUserInfo, YesNo } from 'store/ducks/globalTypes';
import { solutionsOp } from 'store/ducks/Solutions';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { useTranslation } from 'react-i18next';
import { useUnsavedChanges } from 'utils/useUnsavedChanges';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { useSearchableColumn } from 'utils/useSearchableColumn';
import * as Styled from './SolutionDefinition.styled';

const { useToken } = theme;

const TestTable = () => {
  const { token } = useToken();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const userToken = Helpers.getToken();
  const userInfo: IUserInfo = Helpers.getUserInfo().user;
  const {
    lang,
    solutions,
    solutionsToShow,
    solutionsToSave,
    drawTable,
    saveClicked,
    loading,
    filtersData,
    orgContext
  } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    drawTable: state.complianceEst.drawTable,
    solutions: state.solutions.solutions,
    solutionsToShow: state.solutions.solutionsToShow,
    solutionsToSave: state.solutions.solutionsToSave,
    saveClicked: state.complianceEst.saveClicked,
    loading: state.solutions.loading,
    filtersData: state.complianceEst.filtersData,
    orgContext: state.global.hierarchicalLevelsScope
  }));

  const filterParams = Helpers.toApiFilters(orgContext);
  const [approversList, setApproversList] = useState<string[]>([]);
  const [solutionTypes, setSolutionTypes] = useState<string[]>([]);
  const [permitted, setPermitted] = useState<boolean>(false);
  const [localRows, setLocalRows] = useState<{
    [key: number]: { [key: number]: ISolutionsToShow };
  }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(2);
  const [navigationTrigger, setNavigationTrigger] = useState(0);
  const tableRef = useRef<any>(null);

  // Use simple searchable column
  const {
    searchText,
    showSearch,
    filteredData: filteredSolutions,
    column: searchColumn
  } = useSearchableColumn(
    solutions || [],
    'value',
    t('REGULATORY_REQUIREMENTS')
  );

  const handlePlusClick = (row: ISolutionTableRow) => {
    const rowId = row.id;

    const currentRows = {
      ...solutionsToShow,
      ...localRows
    };

    const parentRows = Object.values(currentRows[rowId] || {});

    if (parentRows.length === 0) return;

    const baseRow = parentRows[0] as ISolutionsToShow;

    const newSolId = generateUniqueId();

    const newSolution: ISolutionsToShow = {
      ...baseRow,
      sol_id: newSolId,
      isNew: true,
      SOLUTIONS: {
        sol_id: newSolId,
        bru_id: rowId,
        sol_desc: '',
        sol_type: '',
        approver: '',
        reviewer: '',
        evd_required: YesNo.NO,
        sol_approved: YesNo.NO
      }
    };

    setLocalRows((prev) => ({
      ...prev,
      [rowId]: {
        ...(currentRows[rowId] || {}),
        [newSolId]: newSolution
      }
    }));

    dispatch(complianceEstOp.setSaveDisabled(false));
  };

  const handleStatusChange = (value: YesNo, solId: string | number) => {
    switch (value) {
      case YesNo.YES:
        solutionsOp.approveSolution(solId);
        break;
      case YesNo.NO:
        solutionsOp.rejectSolution(solId);
        break;
      default:
        break;
    }
  };

  const solutionColumns = SolutionColumns({
    solutionTypes,
    approversList,
    permitted,
    dispatch,
    handleStatusChange,
    handlePlusClick,
    showSearch
  });

  // Simple column replacement
  const solutionColumnsWithSearch = useMemo(
    () => [solutionColumns[0], searchColumn, ...solutionColumns.slice(2)],
    [solutionColumns, searchColumn]
  );

  // Use the reusable unsaved changes hook
  const hasUnsavedChanges = useMemo(() => {
    const hasSolutionsChanges = Object.keys(solutionsToSave).length > 0;
    const hasLocalRowsChanges = Object.keys(localRows).length > 0;
    return hasSolutionsChanges || hasLocalRowsChanges;
  }, [solutionsToSave, localRows]);

  useUnsavedChanges({ hasChanges: hasUnsavedChanges });

  useEffect(() => {
    solutionsOp.fetchApproversList().then((data) => {
      const sorted = [...(data?.approver || [])].sort((a, b) =>
        a.localeCompare(b)
      );
      setApproversList(sorted);
    });

    solutionsOp.fetchSolutionTypes(lang).then((data) => {
      const sorted = [...(data?.solution_type || [])].sort((a, b) =>
        a.localeCompare(b)
      );
      setSolutionTypes(sorted);
    });
  }, []);

  useEffect(() => {
    const hasLocalChanges = !Helpers.isObjEmpty(localRows);
    const hasReduxChanges = !Helpers.isObjEmpty(solutionsToSave);

    if (hasLocalChanges || hasReduxChanges) {
      dispatch(complianceEstOp.setSaveDisabled(false));
    } else {
      dispatch(complianceEstOp.setSaveDisabled(true));
    }
  }, [solutionsToSave, localRows, dispatch]);

  useEffect(() => {
    if (userInfo?.entitlements?.length > 0) {
      setPermitted(userInfo.entitlements.includes('Second Approval'));
    } else {
      setPermitted(true);
    }
  }, [userToken]);

  useEffect(() => {
    if (!saveClicked) return;

    const runSave = async () => {
      try {
        const reduxSolutions: ISolution[] = Object.values(
          solutionsToSave
        ).filter((sol) => sol != null) as ISolution[];

        const localSolutions: ISolution[] = Object.values(localRows)
          .flatMap((rowGroup) =>
            Object.values(rowGroup).map((row) => row.SOLUTIONS as ISolution)
          )
          .filter(Boolean);

        const allSolutions: ISolution[] = [
          ...reduxSolutions,
          ...localSolutions
        ];

        const validSolutions = allSolutions.filter((sol) => {
          if (!sol?.bru_id) return false;

          // Check if solution has meaningful content in any field
          return [sol.sol_desc, sol.sol_type, sol.approver].some(
            (field) => field && field.trim() !== ''
          );
        });

        if (validSolutions.length === 0) {
          return;
        }

        const response = await solutionsOp.saveSolutions(
          validSolutions,
          lang,
          solutionsToSave
        );

        notification.success({
          message: 'Success',
          description: response?.data?.message || 'Saved successfully'
        });

        dispatch(solutionsOp.resetSolutionsToSave());
        setLocalRows({});

        const hasFilters = Object.keys(filterParams).length > 0;

        if (orgContext && hasFilters) {
          const data = await solutionsOp.fetchSolutions(lang, filterParams);
          dispatch(solutionsOp.setSolutions(data));
        }
      } catch (error: any) {
        console.error(error);

        notification.error({
          message: 'Save failed',
          description:
            error?.response?.data?.message ||
            error?.message ||
            'Something went wrong'
        });
      } finally {
        complianceEstOp.setSaveClicked(false)(dispatch);
      }
    };

    runSave();
  }, [saveClicked]);

  let uniqueIdCounter = generateUniqueId();

  useEffect(() => {
    setLocalRows({});

    if (!solutions?.length) {
      dispatch(solutionsOp.setSolutionsToShow({}));
      return;
    }

    {
      let newObj: {
        [key: number]: { [key: number | string]: ISolutionsToShow };
      } = {};

      // Get filtered category value
      const categoryFilter = filtersData?.find((filter) =>
        filter.filter.toLowerCase().includes('category')
      );
      const filteredCategory =
        categoryFilter?.options?.length === 1
          ? categoryFilter.options[0]?.value
          : undefined;

      // Use filtered solutions if search is active, otherwise use all solutions
      const dataToProcess =
        searchText.trim() && filteredSolutions.length > 0
          ? filteredSolutions
          : solutions;

      dataToProcess.forEach((item: ISolutions) => {
        const newSolutions: { [key: number | string]: ISolutionsToShow } = {};

        const solEntries = Object.values(item.SOLUTIONS || {});

        if (solEntries.length > 0) {
          solEntries.forEach((solItem) => {
            newSolutions[solItem.sol_id] = {
              ...item,
              category: (filteredCategory as any) || item.category,
              sol_id: solItem.sol_id,
              SOLUTIONS: {
                ...solItem,
                bru_id: item.id
              }
            };
          });
        } else {
          uniqueIdCounter++;
          const placeholderId = `empty_${item.id}`;
          newSolutions[placeholderId] = {
            ...item,
            category: (filteredCategory as any) || item.category,
            sol_id: placeholderId,
            SOLUTIONS: {
              sol_id: generateUniqueId(),
              sol_desc: '',
              sol_type: '',
              sol_approved: YesNo.NO,
              evd_required: YesNo.NO,
              reviewer: '',
              approver: '',
              bru_id: item.id
            }
          };
        }

        newObj[item.id] = newSolutions;
      });

      dispatch(solutionsOp.setSolutionsToShow(newObj));
    }
  }, [solutions, filtersData, searchText, filteredSolutions]);

  // Handle navigation trigger
  useEffect(() => {
    if (navigationTrigger > 0) {
      const tableElement = document.querySelector(
        '.ant-table-pagination .ant-pagination-item-' + currentPage
      );
      if (tableElement) {
        (tableElement as HTMLElement).click();
      }
    }
  }, [navigationTrigger, currentPage]);

  const buildRows = (data: any): ISolutionTableRow[] => {
    const rows: ISolutionTableRow[] = [];

    Object.values(data).forEach((itemGroup: any) => {
      const solutionRows = Object.values(itemGroup).sort((a: any, b: any) => {
        if (a.isNew && !b.isNew) return 1;
        if (!a.isNew && b.isNew) return -1;
        return 0;
      });

      solutionRows.forEach((solution: any, index) => {
        rows.push({
          key: solution.sol_id,
          id: solution.id,
          category: solution.category,
          value: solution.value,
          level: solution.level,
          GAPS: solution.GAPS || {},
          AUDITS: solution.AUDITS || {},
          SOLUTIONS: solution.SOLUTIONS,
          sol_id: solution.sol_id,
          bruRowSpan: index === 0 ? solutionRows.length : 0,
          solRowSpan: 1,
          isLastSolutionRow: index === solutionRows.length - 1,
          originalItem: solution,
          isNew: solution.isNew
        });
      });
    });

    return rows;
  };

  const addRowSpans = (rows: ISolutionTableRow[]) => {
    let i = 0;

    while (i < rows.length) {
      const currentId = rows[i].id;

      let count = 1;
      while (i + count < rows.length && rows[i + count].id === currentId) {
        count++;
      }

      rows[i].bruRowSpan = count;

      for (let j = 1; j < count; j++) {
        rows[i + j].bruRowSpan = 0;
      }

      rows[i].isLastSolutionRow = true;
      for (let j = 0; j < count - 1; j++) {
        rows[i + j].isLastSolutionRow = false;
      }

      i += count;
    }

    return rows;
  };

  const combinedData = {
    ...solutionsToShow,
    ...localRows
  };
  const flatRows = useMemo(() => buildRows(combinedData), [combinedData]);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return flatRows.slice(start, start + pageSize);
  }, [flatRows, currentPage, pageSize]);
  const tableData = useMemo(() => addRowSpans(pagedRows), [pagedRows]);

  return (
    <ComplianceEstLayout>
      <Styled.ContentWrapper token={token}>
        {(() => {
          return drawTable ? (
            <CustomTable
              ref={tableRef}
              drawTable
              token={token}
              columns={solutionColumns}
              dataSource={tableData}
              itemsCount={flatRows.length}
              loading={loading}
              pagination={{
                current: currentPage,
                pageSize,
                total: flatRows.length,
                onChange: (
                  page: React.SetStateAction<number>,
                  size: React.SetStateAction<number>
                ) => {
                  setCurrentPage(page);
                  setPageSize(size);
                }
              }}
            />
          ) : (
            <CustomTable
              drawTable
              itemsCount={0}
              columns={solutionColumnsWithSearch}
              dataSource={[]}
            />
          );
        })()}
      </Styled.ContentWrapper>
    </ComplianceEstLayout>
  );
};

export default TestTable;
